import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { environment } from '@qwyk/hub/environment';
import { GenericQueryMapper } from '@qwyk/core';

import {
    RoutingGuide,
    RoutingGuideItem,
    RoutingGuideItemsResponse,
} from '../store/models/routing-guide.models';

@Injectable({
    providedIn: 'root',
})
export class RoutingGuideService {
    mapper = new GenericQueryMapper();
    /**
     * Fetches the organization's routing guide for the provided product
     * @param product the product
     */
    getRoutingGuide(product: string): Observable<RoutingGuide> {
        return this.http.get<RoutingGuide>(
            `${environment.schedulesBackend}/api/routingguides/${product}`
        );
    }

    /**
     * Fetches the items for the provided routing guide
     *
     * @param routingGuideId the id of the routing guide
     * @param page the page of routing guide items (default = 1)
     * @param pageSize the size of each page (default = 20)
     * @param origin
     * @param destination
     * @param provider
     */
    getRoutingGuideItems(
        routingGuideId: number | undefined,
        page = 1,
        pageSize = 20,
        origin = null,
        destination = null,
        provider = null
    ): Observable<RoutingGuideItemsResponse> {
        const url = `${environment.schedulesBackend}/api/routingguides/${routingGuideId}/items`;

        let params = new HttpParams()
            .set('page', page)
            .set('pageSize', pageSize);

        if (origin) params = params.set('origin', origin);
        if (destination) params = params.set('destination', destination);
        if (provider) params = params.set('provider', provider);

        return this.http.get<RoutingGuideItemsResponse>(url, { params });
    }

    createRoutingGuideItem(
        routingGuideId: number,
        routingGuideItem: RoutingGuideItem
    ): Observable<RoutingGuideItem> {
        return this.http.post<RoutingGuideItem>(
            `${environment.schedulesBackend}/api/routingguides/${routingGuideId}/items`,
            routingGuideItem
        );
    }

    updateRoutingGuideItem(routingGuideItem: RoutingGuideItem) {
        return this.http.post<RoutingGuideItem>(
            `${environment.schedulesBackend}/api/routingguides/${routingGuideItem.routing_guide_id}/items/${routingGuideItem.id}`,
            routingGuideItem
        );
    }

    deleteRoutingGuideItem(routingGuideItem: RoutingGuideItem) {
        return this.http.delete<void>(
            `${environment.schedulesBackend}/api/routingguides/${routingGuideItem.routing_guide_id}/items/${routingGuideItem.id}`
        );
    }

    /**
     * Starts a new routing guide download task on the server
     * @param routingGuide the routing guide to download
     */
    startRoutingGuideDownload(routingGuide: RoutingGuide) {
        return this.http
            .post<void>(
                `${environment.schedulesBackend}/api/routingguides/${routingGuide.id}/download`,
                null
            )
            .pipe(take(1));
    }

    startRoutingGuideUpload(
        routingGuide: RoutingGuide,
        formValue: { routing_guide_file: File }
    ) {
        const payload: FormData = new FormData();
        payload.append(
            'file',
            formValue.routing_guide_file,
            formValue.routing_guide_file.name
        );

        return this.http
            .post<void>(
                `${environment.schedulesBackend}/api/routingguides/${routingGuide.id}/upload`,
                payload
            )
            .pipe(take(1));
    }

    simulateRoutingGuide(payload: {
        product: string;
        origin: string;
        destination: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }): Observable<any> {
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .get<any>(
                    `${environment.schedulesBackend}/api/routingguides/${payload.product}/simulate/${payload.origin}/${payload.destination}`
                )
                .pipe(take(1))
        );
    }

    constructor(private http: HttpClient) {}
}
